import { useEffect } from 'react'
import {
  ConnectionState,
  Room,
  RoomEvent,
  Track,
  type RemoteParticipant,
  type RemoteTrack,
  type RemoteTrackPublication,
} from 'livekit-client'

import { clientLogInfo, clientLogWarn } from '@/src/lib/clientLog'
import { findVisibleScreenShareVideo, hasCurrentScreenShareFrame } from '@/src/lib/screenShareFirstFrame'
import { collectReceiverSnapshot } from '@/src/lib/webrtcStats'
import {
  CODEC_WATCHDOG_SAMPLE_DELAYS_MS,
  COMPLAINT_STREAK_TO_SEND,
  SCREEN_SHARE_CODEC_TOPIC,
  canSampleForCodecWatchdog,
  complaintAllowed,
  decodeScreenShareCodecMessage,
  encodeScreenShareCodecMessage,
  isAgentLikeParticipant,
  noteCodecSwitchSettled,
  noteCodecSwitchStarting,
  shouldComplainAboutCodec,
  updateComplaintStreak,
  type ScreenShareReceiverCounters,
} from '@/src/lib/screenShareCodec'

/** Опрос после стартового всплеска: ловит деградацию по ходу звонка. */
const WATCHDOG_STEADY_INTERVAL_MS = 30_000

/** Время и частота наблюдения за видимым экраном до запроса совместимого кодека. */
const FIRST_FRAME_TIMEOUT_MS = 8000
const FIRST_FRAME_SAMPLE_INTERVAL_MS = 2000
const FIRST_FRAME_MIN_SAMPLES = 3
const FIRST_FRAME_STATS_TIMEOUT_MS = 1000

/** Состояние подписки и независимой проверки первого кадра одного экрана. */
type WatchedTrack = {
  track: RemoteTrack
  publication: RemoteTrackPublication
  publisherIdentity: string
  timers: Array<ReturnType<typeof setTimeout>>
  steadyTimer: ReturnType<typeof setTimeout> | null
  firstFrameTimer: ReturnType<typeof setTimeout> | null
  firstFrameVideo: HTMLVideoElement | null
  firstFrameCallback: number | null
  firstFrameSince: number | null
  firstFrameSamples: number
  firstFrameSeen: boolean
  prev: ScreenShareReceiverCounters | null
  badStreak: number
  complaintPending: boolean
  sentCount: number
  lastSentAt: number | null
}

/** Причина запроса VP8 и доступность исходной статистики получателя. */
type ComplaintDetails = {
  reason: 'undecodable' | 'first-frame-timeout'
  inboundRtpAvailable: boolean
}

export type UseScreenShareCodecWatchdogOptions = {
  room?: Room
  enabled?: boolean
}

/**
 * Следит за декодированием чужой демонстрации и просит VP8 при чёрном экране.
 *
 * История: отказ конкретного H.264-битстрима не виден ни в SDP, ни в `backupCodec`
 * SDK — кодек заявлен и «поддерживается», а кадры не разбираются. Хук сэмплит
 * `getStats` на 1/2/3 секунде после подписки. Дополнительно видимая видеоплитка
 * проверяется на первый кадр, даже если входящая статистика недоступна.
 *
 * @param options.room - комната livekit, пока undefined хук неактивен
 * @param options.enabled - выключатель, по умолчанию включён
 */
export function useScreenShareCodecWatchdog({
  room,
  enabled = true,
}: UseScreenShareCodecWatchdogOptions): void {
  useEffect(() => {
    if (!room || !enabled) {
      return
    }

    const watched = new Map<string, WatchedTrack>()
    // После успешного перехода новый sid уже принадлежит VP8, даже если getStats пуст.
    const switchedVp8Tracks = new Set<string>()
    let disposed = false

    /**
     * Отправляет ограниченную по частоте жалобу автору демонстрации.
     *
     * @param entry - состояние наблюдаемого трека
     * @param trackSid - идентификатор публикации экрана
     * @param counters - совместимые с прежним протоколом счётчики получателя
     * @param details - причина и доступность входящей RTP-статистики
     */
    const sendComplaint = async (
      entry: WatchedTrack,
      trackSid: string,
      counters: ScreenShareReceiverCounters,
      details: ComplaintDetails,
    ): Promise<void> => {
      const now = Date.now()

      if (entry.complaintPending || !complaintAllowed(entry.sentCount, entry.lastSentAt, now)) {
        return
      }

      entry.complaintPending = true

      try {
        await room.localParticipant.publishData(
          encodeScreenShareCodecMessage({
            type: 'SCREEN_SHARE_CODEC_COMPLAINT',
            trackSid,
            publisherIdentity: entry.publisherIdentity,
            reason: details.reason,
            framesReceived: counters.framesReceived,
            framesDecoded: counters.framesDecoded,
            keyFramesDecoded: counters.keyFramesDecoded,
          }),
          {
            reliable: true,
            topic: SCREEN_SHARE_CODEC_TOPIC,
            destinationIdentities: [entry.publisherIdentity],
          },
        )

        entry.sentCount += 1
        entry.lastSentAt = now

        clientLogInfo('screen-share-codec-complaint-sent', {
          room: room.name,
          trackSid,
          publisher: entry.publisherIdentity,
          viewer: room.localParticipant.identity,
          reason: details.reason,
          inboundRtpAvailable: details.inboundRtpAvailable,
          framesReceived: counters.framesReceived,
          framesDecoded: counters.framesDecoded,
          keyFramesDecoded: counters.keyFramesDecoded,
          attempt: entry.sentCount,
        })
      } catch (error) {
        clientLogWarn('screen-share-codec-complaint-failed', {
          room: room.name,
          trackSid,
          reason: details.reason,
          error: error instanceof Error ? error.message : String(error),
        })
      } finally {
        entry.complaintPending = false
      }
    }

    /**
     * Делает один сэмпл: снимок, фильтры честности и решение по дельте.
     */
    const takeSample = async (trackSid: string): Promise<void> => {
      const entry = watched.get(trackSid)

      if (!entry || disposed) {
        return
      }

      try {
        const snapshot = await collectReceiverSnapshot(entry.track, entry.publisherIdentity, trackSid)
        const live = watched.get(trackSid)

        // Пока ждали отчёт, трек могли отписать — проверять нечего.
        if (disposed || live !== entry) {
          return
        }

        if (!snapshot) {
          entry.prev = null
          entry.badStreak = 0

          return
        }

        const counters: ScreenShareReceiverCounters = {
          packetsReceived: snapshot.packetsReceived,
          framesReceived: snapshot.framesReceived,
          framesDecoded: snapshot.framesDecoded,
          keyFramesDecoded: snapshot.keyFramesDecoded,
          pliCount: snapshot.pliCount,
        }
        const mime = snapshot.mimeType?.toLowerCase() ?? ''

        if (
          !canSampleForCodecWatchdog({
            tabVisible: document.visibilityState !== 'hidden',
            subscribed: entry.publication.isSubscribed,
            muted: entry.track.isMuted,
            isVp8: mime === 'video/vp8' || switchedVp8Tracks.has(trackSid),
          }) ||
          room.state !== ConnectionState.Connected ||
          entry.track.streamState !== Track.StreamState.Active ||
          !snapshot.packetsReceivedAvailable ||
          !snapshot.framesReceivedAvailable ||
          !snapshot.framesDecodedAvailable
        ) {
          entry.prev = null
          entry.badStreak = 0

          return
        }

        if (entry.prev) {
          const bad = shouldComplainAboutCodec(entry.prev, counters)

          entry.badStreak = updateComplaintStreak(entry.badStreak, bad)

          // Первая плохая дельта жалобу не вызывает: на второй секунде ключевой
          // кадр ещё может опаздывать, серия из двух подряд — уже отказ.
          if (entry.badStreak >= COMPLAINT_STREAK_TO_SEND) {
            const before = entry.sentCount

            await sendComplaint(entry, trackSid, counters, {
              reason: 'undecodable',
              inboundRtpAvailable: snapshot.inboundRtpAvailable,
            })

            if (entry.sentCount > before) {
              entry.badStreak = 0
            }
          }
        }

        entry.prev = counters
      } catch {
        // Снимок мог не собраться между таймером и опросом — ждём следующий
      }
    }

    /**
     * Сбрасывает наблюдение, когда видеоплитка скрыта или отсоединена.
     *
     * @param entry - состояние наблюдаемого трека
     */
    const resetFirstFrameObservation = (entry: WatchedTrack): void => {
      if (entry.firstFrameVideo && entry.firstFrameCallback !== null) {
        entry.firstFrameVideo.cancelVideoFrameCallback?.(entry.firstFrameCallback)
      }

      entry.firstFrameVideo = null
      entry.firstFrameCallback = null
      entry.firstFrameSince = null
      entry.firstFrameSamples = 0
    }

    /**
     * Завершает проверку после подтверждения первого кадра.
     *
     * @param entry - состояние наблюдаемого трека
     */
    const finishFirstFrameObservation = (entry: WatchedTrack): void => {
      entry.firstFrameSeen = true

      if (entry.firstFrameTimer !== null) {
        clearTimeout(entry.firstFrameTimer)
        entry.firstFrameTimer = null
      }

      resetFirstFrameObservation(entry)
    }

    /**
     * Проверяет, должен ли текущий зритель получать кадры демонстрации.
     *
     * @param entry - состояние наблюдаемого трека
     */
    const canObserveFirstFrame = (entry: WatchedTrack): boolean => {
      return room.state === ConnectionState.Connected &&
        document.visibilityState === 'visible' &&
        !isAgentLikeParticipant(room.localParticipant) &&
        room.localParticipant.permissions?.recorder !== true &&
        entry.publication.isSubscribed &&
        !entry.publication.isMuted &&
        !entry.track.isMuted &&
        entry.track.streamState === Track.StreamState.Active &&
        entry.track.mediaStreamTrack.readyState === 'live'
    }

    /**
     * Ограничивает ожидание статистики, чтобы она не задерживала запрос на VP8.
     *
     * @param entry - состояние наблюдаемого трека
     */
    const readFirstFrameSnapshot = async (entry: WatchedTrack) => {
      let timeout: ReturnType<typeof setTimeout> | undefined

      try {
        return await Promise.race([
          collectReceiverSnapshot(entry.track, entry.publisherIdentity, entry.publication.trackSid),
          new Promise<null>((resolve) => {
            timeout = setTimeout(() => resolve(null), FIRST_FRAME_STATS_TIMEOUT_MS)
          }),
        ])
      } finally {
        if (timeout !== undefined) {
          clearTimeout(timeout)
        }
      }
    }

    /**
     * Подтверждает отсутствие первого кадра и отправляет запрос на VP8.
     *
     * @param entry - состояние наблюдаемого трека
     */
    const reportMissingFirstFrame = async (entry: WatchedTrack): Promise<void> => {
      const video = entry.firstFrameVideo
      const observationSince = entry.firstFrameSince
      const trackSid = entry.publication.trackSid

      if (!video || observationSince === null) {
        return
      }

      const snapshot = await readFirstFrameSnapshot(entry)

      // Новый период видимости нельзя объединять с предыдущим ожиданием.
      if (
        disposed ||
        watched.get(trackSid) !== entry ||
        entry.firstFrameSeen ||
        entry.firstFrameVideo !== video ||
        entry.firstFrameSince !== observationSince
      ) {
        return
      }

      if (!canObserveFirstFrame(entry) || findVisibleScreenShareVideo(entry.track) !== video) {
        resetFirstFrameObservation(entry)

        return
      }

      if (
        hasCurrentScreenShareFrame(video) ||
        (snapshot?.framesDecodedAvailable && snapshot.framesDecoded > 0) ||
        snapshot?.mimeType?.toLowerCase() === 'video/vp8'
      ) {
        finishFirstFrameObservation(entry)

        return
      }

      clientLogWarn('screen-share-codec-first-frame-timeout', {
        room: room.name,
        trackSid,
        publisher: entry.publisherIdentity,
        viewer: room.localParticipant.identity,
        elapsedMs: Math.round(performance.now() - observationSince),
        samples: entry.firstFrameSamples,
        videoReadyState: video.readyState,
        videoWidth: video.videoWidth,
        videoHeight: video.videoHeight,
        mimeType: snapshot?.mimeType ?? entry.publication.mimeType ?? null,
        inboundRtpAvailable: snapshot?.inboundRtpAvailable ?? false,
        framesDecodedAvailable: snapshot?.framesDecodedAvailable ?? false,
      })

      await sendComplaint(entry, trackSid, {
        packetsReceived: snapshot?.packetsReceived ?? 0,
        framesReceived: snapshot?.framesReceived ?? 0,
        framesDecoded: snapshot?.framesDecoded ?? 0,
        keyFramesDecoded: snapshot?.keyFramesDecoded ?? 0,
        pliCount: snapshot?.pliCount ?? 0,
      }, {
        reason: 'first-frame-timeout',
        inboundRtpAvailable: snapshot?.inboundRtpAvailable ?? false,
      })
    }

    /**
     * Проверяет видимую видеоплитку и просит VP8 при длительном отсутствии первого кадра.
     *
     * @param trackSid - идентификатор наблюдаемой публикации
     */
    const checkFirstFrame = async (trackSid: string): Promise<void> => {
      const entry = watched.get(trackSid)

      if (!entry || disposed || entry.firstFrameSeen) {
        return
      }

      if (switchedVp8Tracks.has(trackSid) || entry.publication.mimeType?.toLowerCase() === 'video/vp8') {
        finishFirstFrameObservation(entry)

        return
      }

      const video = canObserveFirstFrame(entry) ? findVisibleScreenShareVideo(entry.track) : null

      if (!video) {
        resetFirstFrameObservation(entry)

        return
      }

      if (video !== entry.firstFrameVideo) {
        resetFirstFrameObservation(entry)
        entry.firstFrameVideo = video

        if (typeof video.requestVideoFrameCallback === 'function') {
          entry.firstFrameCallback = video.requestVideoFrameCallback(() => {
            if (watched.get(trackSid) !== entry || entry.firstFrameVideo !== video) {
              return
            }

            entry.firstFrameCallback = null
            finishFirstFrameObservation(entry)
          })
        }
      }

      if (hasCurrentScreenShareFrame(video)) {
        finishFirstFrameObservation(entry)

        return
      }

      const now = performance.now()

      entry.firstFrameSince ??= now
      entry.firstFrameSamples += 1

      if (now - entry.firstFrameSince < FIRST_FRAME_TIMEOUT_MS || entry.firstFrameSamples < FIRST_FRAME_MIN_SAMPLES) {
        return
      }

      if (entry.complaintPending || !complaintAllowed(entry.sentCount, entry.lastSentAt, Date.now())) {
        return
      }

      await reportMissingFirstFrame(entry)
    }

    /**
     * Повторяет наблюдение, пока первый кадр не появился или трек не снят.
     *
     * @param trackSid - идентификатор наблюдаемой публикации
     */
    const scheduleFirstFrame = (trackSid: string): void => {
      const entry = watched.get(trackSid)

      if (!entry || disposed || entry.firstFrameSeen || entry.firstFrameTimer !== null) {
        return
      }

      entry.firstFrameTimer = setTimeout(() => {
        entry.firstFrameTimer = null

        void checkFirstFrame(trackSid).catch((error) => {
          clientLogWarn('screen-share-codec-first-frame-check-failed', {
            room: room.name,
            trackSid,
            error: error instanceof Error ? error.message : String(error),
          })

          finishFirstFrameObservation(entry)
        }).finally(() => {
          if (watched.get(trackSid) === entry) {
            scheduleFirstFrame(trackSid)
          }
        })
      }, FIRST_FRAME_SAMPLE_INTERVAL_MS)
    }

    /**
     * Ставит повторяющийся опрос трека после стартового всплеска.
     */
    const scheduleSteady = (trackSid: string): void => {
      const entry = watched.get(trackSid)

      if (!entry || disposed) {
        return
      }

      entry.steadyTimer = setTimeout(() => {
        void takeSample(trackSid).then(() => {
          const live = watched.get(trackSid)

          if (live && !disposed) {
            scheduleSteady(trackSid)
          }
        })
      }, WATCHDOG_STEADY_INTERVAL_MS)
    }

    /**
     * Начинает наблюдение: стартовый всплеск и затем редкий опрос.
     */
    const startWatch = (
      track: RemoteTrack,
      publication: RemoteTrackPublication,
      publisherIdentity: string,
    ): void => {
      const trackSid = publication.trackSid

      if (watched.has(trackSid)) {
        return
      }

      const entry: WatchedTrack = {
        track,
        publication,
        publisherIdentity,
        timers: [],
        steadyTimer: null,
        firstFrameTimer: null,
        firstFrameVideo: null,
        firstFrameCallback: null,
        firstFrameSince: null,
        firstFrameSamples: 0,
        firstFrameSeen: false,
        prev: null,
        badStreak: 0,
        complaintPending: false,
        sentCount: 0,
        lastSentAt: null,
      }

      watched.set(trackSid, entry)

      CODEC_WATCHDOG_SAMPLE_DELAYS_MS.forEach((delay) => {
        entry.timers.push(
          setTimeout(() => {
            void takeSample(trackSid)
          }, delay),
        )
      })

      scheduleSteady(trackSid)
      scheduleFirstFrame(trackSid)
    }

    /**
     * Останавливает наблюдение и чистит таймеры трека.
     */
    const stopWatch = (trackSid: string): void => {
      const entry = watched.get(trackSid)

      if (!entry) {
        return
      }

      watched.delete(trackSid)
      resetFirstFrameObservation(entry)
      entry.timers.forEach((timer) => clearTimeout(timer))

      if (entry.firstFrameTimer !== null) {
        clearTimeout(entry.firstFrameTimer)
        entry.firstFrameTimer = null
      }

      entry.timers = []

      if (entry.steadyTimer !== null) {
        clearTimeout(entry.steadyTimer)
        entry.steadyTimer = null
      }
    }

    const handleTrackSubscribed = (
      track: RemoteTrack,
      publication: RemoteTrackPublication,
      participant: RemoteParticipant,
    ): void => {
      if (publication.source !== Track.Source.ScreenShare || track.kind !== Track.Kind.Video) {
        return
      }

      startWatch(track, publication, participant.identity)
    }

    const handleTrackUnsubscribed = (
      _track: RemoteTrack,
      publication: RemoteTrackPublication,
    ): void => {
      if (publication.source !== Track.Source.ScreenShare) {
        return
      }

      stopWatch(publication.trackSid)
    }

    /**
     * Обрабатывает итог перехода: заморозка пина и сброс жалоб по старому треку.
     *
     * Новый sid придёт обычной подпиской и заведёт свежее наблюдение —
     * детектор для него включится заново, но не раньше ~3 с после подписки.
     */
    const handleDataReceived = (payload: Uint8Array, participant?: RemoteParticipant): void => {
      const message = decodeScreenShareCodecMessage(payload)

      if (!message) {
        return
      }

      if (message.type === 'SCREEN_SHARE_CODEC_SWITCHING') {
        noteCodecSwitchStarting(message.publisherIdentity)

        return
      }

      if (message.type === 'SCREEN_SHARE_CODEC_SWITCHED') {
        noteCodecSwitchSettled(message.publisherIdentity)

        if (
          message.ok &&
          participant?.identity === message.publisherIdentity &&
          message.codecTo.toLowerCase() === 'vp8' &&
          message.newTrackSid
        ) {
          switchedVp8Tracks.add(message.newTrackSid)

          const nextEntry = watched.get(message.newTrackSid)

          if (nextEntry) {
            finishFirstFrameObservation(nextEntry)
          }
        }

        stopWatch(message.oldTrackSid)

        clientLogInfo('screen-share-codec-ack-received', {
          room: room.name,
          oldTrackSid: message.oldTrackSid,
          newTrackSid: message.newTrackSid,
          publisher: message.publisherIdentity,
          codecFrom: message.codecFrom,
          codecTo: message.codecTo,
          ok: message.ok,
        })
      }
    }

    // Хук может смонтироваться позже подписки: перебираем то, что уже смотрим.
    room.remoteParticipants.forEach((participant) => {
      participant.trackPublications.forEach((publication) => {
        if (publication.source !== Track.Source.ScreenShare) {
          return
        }

        const videoTrack = publication.videoTrack

        if (
          publication.isSubscribed &&
          videoTrack &&
          'receiver' in videoTrack &&
          videoTrack.kind === Track.Kind.Video
        ) {
          startWatch(videoTrack, publication, participant.identity)
        }
      })
    })

    /** Сбрасывает накопленное время при уходе зрителя со вкладки. */
    const handleVisibilityChange = (): void => {
      if (document.visibilityState !== 'visible') {
        watched.forEach(resetFirstFrameObservation)
      }
    }

    /** Сбрасывает ожидание, пока соединение комнаты восстанавливается. */
    const handleReconnecting = (): void => {
      watched.forEach(resetFirstFrameObservation)
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    room
      .on(RoomEvent.Reconnecting, handleReconnecting)
      .on(RoomEvent.TrackSubscribed, handleTrackSubscribed)
      .on(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed)
      .on(RoomEvent.DataReceived, handleDataReceived)

    return () => {
      disposed = true

      document.removeEventListener('visibilitychange', handleVisibilityChange)

      room
        .off(RoomEvent.Reconnecting, handleReconnecting)
        .off(RoomEvent.TrackSubscribed, handleTrackSubscribed)
        .off(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed)
        .off(RoomEvent.DataReceived, handleDataReceived)

      Array.from(watched.keys()).forEach(stopWatch)
      switchedVp8Tracks.clear()
    }
  }, [room, enabled])
}
